import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
    ArrowRight, Loader2, CheckCircle, AlertTriangle, Shield, Target,
    Heart, Zap, Brain, ChevronRight, Sparkles, Award, Radar,
    FileSignature, TreePine, Scan
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CH2_MODULES = [
    { id: 'mapa_amenaza', name: 'Mapa de amenaza', icon: AlertTriangle, color: '#E8B27A' },
    { id: 'radar_exigencia', name: 'Radar de exigencia', icon: Radar, color: '#3E5F6E' },
    { id: 'contrato_oculto', name: 'Contrato oculto', icon: FileSignature, color: '#7A9BA8' },
    { id: 'arbol_temor', name: 'Árbol de temor', icon: TreePine, color: '#5FAF9D' },
    { id: 'radiografia', name: 'Radiografía', icon: Scan, color: '#3E5F6E' }
];

export default function ResumenCapitulo2() {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/profile`, { withCredentials: true });
                setProfile(data);
            } catch {
                toast.error('Error al cargar datos');
                navigate('/usuario');
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [navigate]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" />
            </div>
        );
    }

    const completedModules = profile?.modules_completed || [];
    const ch2Completed = CH2_MODULES.filter(m => completedModules.some(cm => cm.module_id === m.id));
    const completionRate = (ch2Completed.length / CH2_MODULES.length) * 100;
    const aiSummaries = profile?.ai_summaries || [];
    const radiografia = profile?.radiografia_perfil;
    const decisionCorrectiva = profile?.decision_correctiva;

    const amenazaSummary = aiSummaries.find(s => s.module === 'mapa_amenaza');
    const radarSummary = aiSummaries.find(s => s.module === 'radar_exigencia');
    const contratoSummary = aiSummaries.find(s => s.module === 'contrato_oculto');
    const arbolSummary = aiSummaries.find(s => s.module === 'arbol_temor');
    const radiografiaSummary = aiSummaries.find(s => s.module === 'radiografia');

    return (
        <div className="min-h-screen bg-[#F5F7F8]">
            <Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <img src="/logo-senda.png" alt="SENDA" className="h-9 w-auto" />
                            <div>
                                <h1 className="text-xl font-semibold text-[#1F2A30] font-['Outfit']" data-testid="ch2-summary-title">Resumen Capítulo 2</h1>
                                <p className="text-sm text-[#4A5B63]">Radiografía integrada SENDA</p>
                            </div>
                        </div>
                        <Link to="/usuario">
                            <Button variant="ghost" className="text-[#3E5F6E] hover:bg-[#3E5F6E]/5">Volver al inicio</Button>
                        </Link>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Hero */}
                <div className="relative overflow-hidden bg-gradient-to-br from-[#3E5F6E] via-[#3E5F6E] to-[#2F4A57] rounded-3xl p-8 lg:p-12 mb-8 shadow-2xl">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[#5FAF9D]/20 rounded-full blur-3xl" />
                    <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-4">
                                <Award className="h-6 w-6 text-[#5FAF9D]" />
                                <span className="text-[#5FAF9D] font-medium uppercase tracking-wide text-sm">Capítulo 2 Completado</span>
                            </div>
                            <h2 className="text-3xl lg:text-4xl font-light text-white font-['Outfit'] mb-4">Tu radiografía interna</h2>
                            <p className="text-white/80 text-lg max-w-xl">Ahora conoces la amenaza que te activa, la defensa que usas y el temor que está en la raíz de tu evitación.</p>
                        </div>
                        <div className="flex flex-col items-center">
                            <div className="relative w-40 h-40">
                                <svg className="w-full h-full transform -rotate-90">
                                    <circle cx="80" cy="80" r="70" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="12" />
                                    <circle cx="80" cy="80" r="70" fill="none" stroke="#5FAF9D" strokeWidth="12" strokeLinecap="round" strokeDasharray={`${completionRate * 4.4} 440`} className="transition-all duration-1000" />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-4xl font-bold text-white font-['Outfit']">{Math.round(completionRate)}%</span>
                                    <span className="text-white/60 text-sm">completado</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Module Progress */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    <Card className="bg-white border-[#DCE3E8] shadow-sm">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-[#1F2A30]">
                                <Scan className="h-5 w-5 text-[#3E5F6E]" />
                                Progreso por módulo
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {CH2_MODULES.map((module) => {
                                    const isCompleted = completedModules.some(cm => cm.module_id === module.id);
                                    const Icon = module.icon;
                                    return (
                                        <div key={module.id} className="flex items-center gap-4">
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isCompleted ? 'bg-[#5FAF9D]' : 'bg-[#DCE3E8]'}`}>
                                                {isCompleted ? <CheckCircle className="h-5 w-5 text-white" /> : <Icon className="h-5 w-5 text-[#7A8A93]" />}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className={`text-sm font-medium ${isCompleted ? 'text-[#1F2A30]' : 'text-[#7A8A93]'}`}>{module.name}</span>
                                                    <span className={`text-xs ${isCompleted ? 'text-[#5FAF9D]' : 'text-[#7A8A93]'}`}>{isCompleted ? '100%' : '0%'}</span>
                                                </div>
                                                <div className="h-2 bg-[#DCE3E8] rounded-full overflow-hidden">
                                                    <div className="h-full bg-[#5FAF9D] transition-all duration-500" style={{ width: isCompleted ? '100%' : '0%' }} />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Integrated Profile */}
                    <Card className="bg-white border-[#DCE3E8] shadow-sm">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-[#1F2A30]">
                                <Sparkles className="h-5 w-5 text-[#5FAF9D]" />
                                Perfil integrado
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            {radiografia?.amenaza_central && (
                                <div className="p-3 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                                    <div className="flex items-center gap-2 mb-1">
                                        <AlertTriangle className="h-4 w-4 text-[#E8B27A]" />
                                        <p className="text-xs font-semibold text-[#E8B27A] uppercase">Amenaza central</p>
                                    </div>
                                    <p className="text-[#1F2A30] text-sm font-medium">{radiografia.amenaza_central}</p>
                                </div>
                            )}
                            {radiografia?.defensa_principal && (
                                <div className="p-3 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Shield className="h-4 w-4 text-[#3E5F6E]" />
                                        <p className="text-xs font-semibold text-[#3E5F6E] uppercase">Defensa principal</p>
                                    </div>
                                    <p className="text-[#1F2A30] text-sm font-medium">{radiografia.defensa_principal}</p>
                                </div>
                            )}
                            {radiografia?.temor_raiz && (
                                <div className="p-3 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Heart className="h-4 w-4 text-[#D07F7E]" />
                                        <p className="text-xs font-semibold text-[#D07F7E] uppercase">Temor raíz</p>
                                    </div>
                                    <p className="text-[#1F2A30] text-sm font-medium">{radiografia.temor_raiz}</p>
                                </div>
                            )}
                            {decisionCorrectiva && (
                                <div className="p-3 bg-gradient-to-r from-[#5FAF9D]/10 to-transparent rounded-xl border border-[#5FAF9D]/20">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Zap className="h-4 w-4 text-[#5FAF9D]" />
                                        <p className="text-xs font-semibold text-[#5FAF9D] uppercase">Decisión correctiva</p>
                                    </div>
                                    <p className="text-[#1F2A30] text-sm font-medium">{decisionCorrectiva}</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* AI Insights */}
                {radiografiaSummary?.insight_usuario && (
                    <Card className="bg-white border-[#DCE3E8] shadow-sm mb-8">
                        <CardContent className="pt-6">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-[#5FAF9D]/20 rounded-full flex items-center justify-center shrink-0">
                                    <Brain className="h-5 w-5 text-[#5FAF9D]" />
                                </div>
                                <div>
                                    <p className="text-xs font-semibold text-[#5FAF9D] uppercase mb-2">Reflexión integradora</p>
                                    <p className="text-[#1F2A30] italic">"{radiografiaSummary.insight_usuario}"</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* CTA Chapter 3 */}
                <Card className="bg-gradient-to-r from-[#3E5F6E] to-[#2F4A57] border-0 shadow-xl overflow-hidden" data-testid="ch3-cta">
                    <CardContent className="p-8 lg:p-12">
                        <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
                            <div className="text-center lg:text-left">
                                <div className="flex items-center justify-center lg:justify-start gap-2 mb-3">
                                    <div className="w-8 h-8 bg-[#5FAF9D] rounded-lg flex items-center justify-center">
                                        <span className="text-white font-bold text-sm">3</span>
                                    </div>
                                    <span className="text-[#5FAF9D] font-medium uppercase tracking-wide text-sm">Siguiente paso</span>
                                </div>
                                <h3 className="text-2xl lg:text-3xl font-light text-white font-['Outfit'] mb-2">Capítulo 3: Foco y Acción</h3>
                                <p className="text-white/70 max-w-lg">Organiza tus frentes abiertos, filtra el impacto real y diseña tu primera acción inevitable.</p>
                            </div>
                            <Link to="/usuario">
                                <Button className="bg-[#5FAF9D] hover:bg-[#4A9A89] text-white px-8 py-4 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all text-lg">
                                    Comenzar Capítulo 3
                                    <ChevronRight className="h-5 w-5 ml-2" />
                                </Button>
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
